import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import { toSearchResultsWithPaginationPage } from 'lib/links';
import { hasValidOffersResults } from 'lib/offer';
import placeholder from 'lib/placeholder';

import Loading from './Loading';

import { SEARCH_RESULTS_TYPES } from 'constants/searchResults';

const NoResultsFound = dynamic(() => import('./NoResultsFound'), {
  ssr: false,
});
const ArticlesResults = dynamic(() => import('./ArticlesResults'), {
  ssr: false,
});
const CouponsResults = dynamic(() => import('./CouponsResults'), {
  ssr: false,
});
const GeneralResults = dynamic(() => import('./GeneralResults'), {
  ssr: false,
});
const OffersResults = dynamic(() => import('./OffersResults'), {
  ssr: false,
});
const MembersResults = dynamic(() => import('./MembersResults'), {
  ssr: false,
});
const ThreadsResults = dynamic(() => import('./ThreadsResults'), {
  ssr: false,
});

const onPaginationChange = (page) => toSearchResultsWithPaginationPage(page);

const Results = ({
  gaEventCategory,
  hasResults,
  isLoading,
  searchQs,
  searchResults = {},
  searchResultsSort = '',
  searchResultsType = null,
  onFilterButtonClick,
}) => {
  if (isLoading) {
    return <Loading />;
  }

  if (!hasResults) {
    return (
      <NoResultsFound
        gaEventCategory={gaEventCategory}
        searchQuery={searchQs.searchQuery}
        searchQuerySuggestedCorrection={
          searchResults?.searchQuerySuggestedCorrection
        }
      />
    );
  }

  return (
    <div className="mt-4 flex flex-col gap-y-4">
      {searchResultsType?.PARAM === SEARCH_RESULTS_TYPES.GENERAL.PARAM &&
        hasResults && (
          <GeneralResults
            gaEventCategory={gaEventCategory}
            searchQuery={searchQs.searchQuery}
            onFilterButtonClick={onFilterButtonClick}
            {...searchResults}
          />
        )}
      {hasValidOffersResults(searchResultsType?.PARAM) &&
        searchResults.offers?.length > 0 && (
          <OffersResults
            gaEventCategory={gaEventCategory}
            searchQuery={searchQs.searchQuery}
            searchResultsPage={searchQs.searchResultsPage}
            searchResultsSort={searchResultsSort}
            searchResultsType={
              SEARCH_RESULTS_TYPES.ACTIVE_OFFERS ||
              SEARCH_RESULTS_TYPES.FINISHED_OFFERS
            }
            title={
              searchResultsType?.PARAM ===
              SEARCH_RESULTS_TYPES.ACTIVE_OFFERS.PARAM
                ? placeholder('titles.activeOffers')
                : placeholder('titles.finishedOffers')
            }
            withMainHeadingSection={!searchQs.alias}
            withSortSelect
            onPaginationChange={onPaginationChange}
            {...searchResults}
          />
        )}
      {searchResultsType?.PARAM === SEARCH_RESULTS_TYPES.ARTICLES.PARAM &&
        searchResults?.articles.length > 0 && (
          <ArticlesResults
            gaEventCategory={gaEventCategory}
            searchResultsPage={searchQs.searchResultsPage}
            searchResultsSort={searchResultsSort}
            searchResultsType={SEARCH_RESULTS_TYPES.ARTICLES}
            title={placeholder('titles.articles')}
            withSortSelect
            onPaginationChange={onPaginationChange}
            {...searchResults}
          />
        )}
      {searchResultsType?.PARAM === SEARCH_RESULTS_TYPES.COUPONS.PARAM &&
        searchResults?.coupons.length > 0 && (
          <CouponsResults
            gaEventCategory={gaEventCategory}
            searchResultsPage={searchQs.searchResultsPage}
            searchResultsSort={searchResultsSort}
            searchResultsType={SEARCH_RESULTS_TYPES.COUPONS}
            title={placeholder('titles.coupons')}
            withSortSelect
            onPaginationChange={onPaginationChange}
            {...searchResults}
          />
        )}
      {searchResultsType?.PARAM === SEARCH_RESULTS_TYPES.THREADS.PARAM &&
        searchResults?.threads.length > 0 && (
          <ThreadsResults
            gaEventCategory={gaEventCategory}
            searchResultsPage={searchQs.searchResultsPage}
            searchResultsSort={searchResultsSort}
            searchResultsType={SEARCH_RESULTS_TYPES.THREADS}
            title={placeholder('titles.forumShort')}
            withSortSelect
            onPaginationChange={onPaginationChange}
            {...searchResults}
          />
        )}
      {searchResultsType?.PARAM === SEARCH_RESULTS_TYPES.USERS.PARAM &&
        searchResults?.users.length > 0 && (
          <MembersResults
            gaEventCategory={gaEventCategory}
            searchResultsPage={searchQs.searchResultsPage}
            searchResultsSort={searchResultsSort}
            searchResultsType={SEARCH_RESULTS_TYPES.USERS}
            title={placeholder('titles.communityMembers')}
            withSortSelect
            onPaginationChange={onPaginationChange}
            {...searchResults}
          />
        )}
    </div>
  );
};

Results.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  hasResults: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  searchQs: PropTypes.shape({
    alias: PropTypes.string,
    sort: PropTypes.string,
    legacySearchResultsTypeParam: PropTypes.string,
    searchResultsPage: PropTypes.number,
    searchQuery: PropTypes.string,
    aliasHash: PropTypes.string,
    searchResultsTypeParam: PropTypes.string,
  }).isRequired,
  searchResults: PropTypes.shape(),
  searchResultsSort: PropTypes.string,
  searchResultsType: PropTypes.shape({
    NAME: PropTypes.string.isRequired,
    QUERY: PropTypes.string.isRequired,
    TYPE: PropTypes.string,
    BUTTON_TEXT: PropTypes.string,
    GA_EVENT_ACTION_PREFIX: PropTypes.string,
    GENERAL_RESULTS_LIMIT: PropTypes.number.isRequired,
    SORT_OPTIONS: PropTypes.arrayOf(
      PropTypes.shape({
        isDefaultOption: PropTypes.bool.isRequired,
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
      })
    ),
  }),
  onFilterButtonClick: PropTypes.func.isRequired,
};

export default Results;
